import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  CircularProgress,
  useTheme,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { postAiChatService } from '../../services/controlTiempos';

interface Message {
  id: number;
  text: string;
  isBot: boolean;
}

export const AiChat = () => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: Date.now(),
      text: '¡Hola! Soy la IA de tu hotel. Pregúntame sobre los tiempos de los empleados.',
      isBot: true,
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  const handleToggle = () => setIsOpen(!isOpen);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = { id: Date.now(), text: input, isBot: false };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await postAiChatService(userMessage.text);

      let botResponseText = 'Lo siento, hubo un error al consultar a la IA.';
      if (response && response.ok && response.text) {
        botResponseText = response.text;
      } else if (response && response.message) {
        botResponseText = response.message;
      }

      setMessages((prev) => [...prev, { id: Date.now(), text: botResponseText, isBot: true }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), text: 'Error de conexión con la IA.', isBot: true },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Fab
        color="primary"
        aria-label="chat"
        onClick={handleToggle}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 9999,
          boxShadow: theme.shadows[8],
        }}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </Fab>

      {isOpen && (
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            bottom: 90,
            right: 24,
            width: 350,
            height: 500,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 9999,
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <SmartToyIcon />
            <Typography
              variant="h6"
              component="div"
              sx={{ flexGrow: 1, fontSize: '1rem', fontWeight: 'bold' }}
            >
              Asistente IA
            </Typography>
            <IconButton size="small" onClick={handleToggle} sx={{ color: 'inherit' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages Area */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, backgroundColor: '#f5f5f5' }}>
            <List sx={{ p: 0 }}>
              {messages.map((msg) => (
                <ListItem
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    flexDirection: msg.isBot ? 'row' : 'row-reverse',
                    alignItems: 'flex-start',
                    gap: 1,
                    mb: 1,
                    px: 0,
                    py: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: msg.isBot
                        ? theme.palette.primary.light
                        : theme.palette.secondary.light,
                      color: msg.isBot
                        ? theme.palette.primary.contrastText
                        : theme.palette.secondary.contrastText,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      maxWidth: '80%',
                      boxShadow: theme.shadows[1],
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {msg.text}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
              {isLoading && (
                <ListItem sx={{ px: 0, py: 1 }}>
                  <CircularProgress size={20} />
                </ListItem>
              )}
              <div ref={messagesEndRef} />
            </List>
          </Box>

          {/* Input Area */}
          <Box
            sx={{
              p: 1.5,
              backgroundColor: theme.palette.background.default,
              display: 'flex',
              gap: 1,
            }}
          >
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Escribe tu mensaje..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSend();
              }}
              disabled={isLoading}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              sx={{
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
                '&:hover': { backgroundColor: theme.palette.primary.main },
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      )}
    </>
  );
};
